import React, {useEffect, useState} from 'react';
import {injectIntl} from 'react-intl';
import axios from 'axios';
import TopCities from '@Components/TopCities';
import SearchForm from '@Components/SearchForm';
import RelatedJobs from '@Components/RelatedJobs';
import JobAlertsForm from '@Components/JobAlertsForm';
import RelatedArticles from '@Components/RelatedArticles';
import {List} from 'react-content-loader';
import PageHeader from '@Components/PageHeader';
import {Helmet} from 'rnd-helmet';
import {handleSalaryPage} from 'dataLayerHelper';
import {useLocation} from 'react-router';
import capitalizeText from 'capitalizeText';
import {Route} from 'react-router-dom';
import NotFound from '@Pages/NotFound';

const SalaryDetails = ({intl, staticContext, match}) => {
  const transformSalary = (salary) => {
    return parseFloat(salary.replace('$', '').replace(',', '').replace('/yr', '').replace('/hr', ''));
  };
  const location = useLocation();
  const dataLayerPageType = 'salary_details';
  const dataLayerPageLocation = location.pathname + location.search;
  const [contentLoaded, setContentLoaded] = useState((staticContext && staticContext.data));
  let initialData = {};
  if (staticContext && staticContext.data) {
    initialData = staticContext.data;
  } else if (typeof window !== 'undefined' && window.__ROUTE_DATA__) {
    setContentLoaded(true);
    initialData = window.__ROUTE_DATA__;
    if (process.env.REACT_APP_ENABLE_GTM === 'true') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'pageview',
        virtualPage: location.pathname + location.search,
        // Note: relatedJobs will be mutated for having dataLayer properties.
        ...handleSalaryPage(initialData, dataLayerPageType, dataLayerPageLocation),
      });
    }
    delete window.__ROUTE_DATA__;
  }

  const [query, setQuery] = useState(initialData.query);
  const [configurations, setConfigurations] = useState(initialData.configurations);
  const [jobData, setJobData] = useState(initialData.jobData);
  const [relatedJobsData, setRelatedJobsData] = useState(initialData.relatedJobs);
  const [relatedArticles, setRelatedArticles] = useState(initialData.relatedArticles);
  const canonicalURL = `${process.env.REACT_APP_BASE_URL}${location.pathname}`;

  useEffect(() => {
    if (!jobData) {
      axios.post(`${process.env.REACT_APP_API_PREFIX}/get-callback`, {
        callback: '@Callbacks/getJobDetails',
        currentRoute: {...match, ...{routeName: 'salary-details'}},
        data: {},
      }).then((response) => {
        setContentLoaded(true);
        if (response.data) {
          setJobData(response.data.jobData);
          setConfigurations(response.data.configurations);
          setQuery(response.data.query);
        }
      });
    }
  }, []);

  useEffect(() => {
    if ((!relatedJobsData || !relatedArticles) && jobData && Object.keys(jobData).length > 0) {
      const relatedJobs = axios.post(`${process.env.REACT_APP_API_PREFIX}/get-callback`, {
        callback: '@Callbacks/getRelatedJobs',
        currentRoute: {},
        data: {
          jobTitle: jobData?.jobTitle,
        },
      });
      const relatedArticles = axios.post(`${process.env.REACT_APP_API_PREFIX}/get-callback`, {
        callback: '@Callbacks/getRelatedArticles',
        currentRoute: {},
        data: {
          keyword: jobData?.jobTitle,
        },
      });
      Promise.all([relatedJobs, relatedArticles])
          .then(([relatedJobsResponse, relatedArticlesResponse]) => {
            // Mutate data before saving to state.
            handleSalaryPage({relatedJobs: relatedJobsResponse.data}, dataLayerPageType, dataLayerPageLocation);
            if (relatedArticlesResponse.data || relatedJobsResponse.data) {
              setRelatedArticles(relatedArticlesResponse.data);
              setRelatedJobsData(relatedJobsResponse.data);
            }
          });
    }
  }, [jobData]);

  const relatedArticlesData = relatedArticles && relatedArticles.hits ? relatedArticles.hits.hits : [];

  if (contentLoaded && (!jobData || (Object.getPrototypeOf(jobData) === Object.prototype && Object.keys(jobData).length === 0))) {
    return (
      <Route render={({staticContext}) => {
        if (staticContext) staticContext.status = 404;
        return <NotFound/>;
      }}/>
    );
  }
  if (!jobData) {
    return (
      <>
        <List />
      </>
    );
  }
  const breadcrumbItems = [
    {
      url: `/salary/${match.params.jobTitle}/`,
      title: jobData?.jobTitle.toLowerCase(),
    },
  ];
  const duration = (jobData.topCities[0].salaryType && jobData.topCities[0].salaryType === 'hourly') ? 'P1H' : 'P1Y';
  let minSalary = 0;
  let maxSalary = 0;
  let median = 0;
  if (jobData.topCities) {
    jobData.topCities.forEach((topCity) => {
      const salary = transformSalary(topCity.topSalary);
      if (minSalary === 0 || minSalary > salary) {
        minSalary = salary;
      }
      if (maxSalary === 0 || maxSalary < salary) {
        maxSalary = salary;
      }
      median += salary;
    });
    median /= jobData.topCities.length;
    median = Math.round(median * 100) / 100;
  }
  const OccupationScript = {
    '@context': 'http://schema.org',
    '@type': 'Occupation',
    'name': jobData.jobTitle,
    'description': jobData.description,
    'estimatedSalary': [
      {
        '@type': 'MonetaryAmountDistribution',
        'name': 'base',
        'currency': 'USD',
        'duration': duration,
        'percentile10': minSalary ? minSalary : '',
        'percentile90': maxSalary ? maxSalary : '',
        'median': median ? median : '',
      },
    ],
    'occupationLocation': [
      {
        '@type': 'Country',
        'name': 'United States',
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage({id: 'TalentDetails.Page.Meta.Title'}, {
            title: capitalizeText(jobData.jobTitle),
            sitename: process.env.REACT_APP_SITENAME,
          })}
        </title>
        <link rel="canonical" href={canonicalURL} />
        <meta name="description" content={intl.formatMessage({id: 'TalentDetails.Page.Meta.Description'}, {
          title: capitalizeText(jobData.jobTitle),
        })} />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{'__html': JSON.stringify(OccupationScript)}}/>
      <div>
        <PageHeader
          title={jobData?.jobTitle}
          breadcrumbItems={breadcrumbItems}
          description={jobData?.description}
          fullWidth={false}
          imageSrc='/salary-app/assets/images/JobTitles_NavyandTurqBG.svg'
          imageAlt={intl.formatMessage({id: 'Salary.Page.Illustration.Alt'})} />
        <TopCities title={jobData?.header} cities={jobData?.topCities} baseUrl={`/salary/${match.params.jobTitle}/`} />
        <SearchForm title={jobData?.jobTitle} searchParams={query} baseUrl={`/salary/${match.params.jobTitle}/`} />
        {relatedJobsData && relatedJobsData.results && relatedJobsData.results.length > 0 &&
          <RelatedJobs configurations={configurations} relatedJobsData={relatedJobsData} />
        }
        <JobAlertsForm keyword={jobData?.jobTitle} />
        {
          relatedArticlesData.length ? <RelatedArticles relatedArticlesData={relatedArticlesData}/> : null
        }
      </div>
    </>
  );
};

export default injectIntl(SalaryDetails);
